/* Twenty fictional examples, twenty image nodes. Only fully offscreen originals recycle. */
(() => {
  'use strict';
  if (window.__r25Reviews) return;
  window.__r25Reviews = true;
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  function init(scope = document) {
    scope.querySelectorAll('[data-reviews-marquee]').forEach(section => {
      if (section.dataset.marqueeReady || !window.IntersectionObserver || !window.ResizeObserver) return;
      section.dataset.marqueeReady = 'true';
      const events = new AbortController(), signal = events.signal;
      const button = section.querySelector('[data-reviews-pause]');
      let pausedByUser = false, inView = false, frame = 0, previousTime = 0, destroyed = false;
      const rows = [...section.querySelectorAll('[data-reviews-row]')].map(viewport => {
        const original = viewport.querySelector('[data-reviews-original]');
        return { viewport, original, cards:[...original.children], stride:0, cycle:0, position:0, loop:false, visible:false, focus:false, direction:Number(viewport.dataset.direction) || 1, duration:Number(viewport.dataset.duration) || 72 };
      });
      const running = () => !destroyed && section.isConnected && !pausedByUser && !motion.matches && inView && !document.hidden;
      const label = () => { if (!button) return; button.hidden = motion.matches; button.textContent = pausedByUser ? button.dataset.playLabel : button.dataset.pauseLabel; button.setAttribute('aria-pressed', String(pausedByUser)); };
      const stop = () => { cancelAnimationFrame(frame); frame = 0; previousTime = 0; };
      const resume = () => { if (running() && rows.some(row => row.visible && !row.focus && row.loop) && !frame) frame = requestAnimationFrame(tick); };
      const pauseManually = () => { pausedByUser = true; stop(); label(); };
      function tick(time) {
        frame = 0;
        if (!running()) { previousTime = 0; return; }
        const delta = previousTime ? Math.min(time - previousTime, 40) : 0;
        previousTime = time;
        let active = false;
        rows.forEach(row => {
          if (!row.visible || row.focus || !row.loop) return;
          active = true;
          // Two spare strides keep both the removed and reinserted card outside the viewport.
          // Equal scroll compensation preserves every visible card's position. No per-frame layout reads.
          if (row.direction < 0 && row.position < row.stride) {
            row.original.prepend(row.original.lastElementChild);
            row.position += row.stride;
          }
          row.position += row.direction * row.cycle * delta / (row.duration * 1000);
          while (row.direction > 0 && row.position >= row.stride) {
            row.original.append(row.original.firstElementChild);
            row.position -= row.stride;
          }
          row.viewport.scrollLeft = row.position;
        });
        if (active) frame = requestAnimationFrame(tick); else previousTime = 0;
      }
      const seen = new Set();
      const cardObservers = rows.map(row => new IntersectionObserver(entries => entries.forEach(entry => {
        if (!entry.isIntersecting || !inView) return;
        const card = entry.target, id = card.dataset.exampleId;
        if (!seen.has(id)) { card.classList.add('is-entered'); seen.add(id); }
      }), { root:row.viewport, threshold:0.05 }));
      const measure = () => {
        rows.forEach(row => {
          const width = row.original.firstElementChild?.getBoundingClientRect().width || 0;
          const gap = parseFloat(getComputedStyle(row.original).columnGap) || 0;
          const stride = width + gap, total = stride * row.cards.length - gap;
          const loop = !motion.matches && width > 0 && total > row.viewport.clientWidth + 2 * stride;
          const position = loop && row.loop && row.stride ? row.viewport.scrollLeft / row.stride * stride : 0;
          row.stride = stride; row.cycle = stride * row.cards.length; row.loop = loop;
          row.viewport.classList.toggle('is-recycling', loop);
          row.position = position; row.viewport.scrollLeft = position;
        }); previousTime = 0; resume();
      };
      const configure = () => {
        stop();
        rows.forEach((row, index) => {
          cardObservers[index].disconnect();
          // Restore authored reading order for reduced motion and section reconfiguration.
          row.cards.forEach(card => row.original.append(card));
          row.cards.forEach(card => cardObservers[index].observe(card));
          row.loop = false; row.position = 0; row.viewport.scrollLeft = 0;
        }); label(); measure();
      };
      rows.forEach(row => {
        row.viewport.addEventListener('focusin', () => { row.focus = true; }, {signal});
        row.viewport.addEventListener('focusout', event => { if (!row.viewport.contains(event.relatedTarget)) { row.focus = false; row.position = row.viewport.scrollLeft; previousTime = 0; resume(); } }, {signal});
        row.viewport.addEventListener('pointerdown', pauseManually, {signal});
        row.viewport.addEventListener('wheel', event => { if (Math.abs(event.deltaX) > 1 || event.shiftKey) pauseManually(); }, {passive:true, signal});
        row.viewport.addEventListener('scroll', () => { if (pausedByUser || row.focus || motion.matches) row.position = row.viewport.scrollLeft; }, {passive:true, signal});
        row.viewport.addEventListener('keydown', event => {
          if (!['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
          event.preventDefault(); pauseManually();
          const limit = Math.max(0, row.viewport.scrollWidth - row.viewport.clientWidth);
          row.viewport.scrollLeft = event.key === 'Home' ? 0 : event.key === 'End' ? limit : Math.max(0, Math.min(limit, row.viewport.scrollLeft + (event.key === 'ArrowRight' ? row.stride : -row.stride)));
        }, {signal});
      });
      button?.addEventListener('click', () => { pausedByUser = !pausedByUser; label(); if (pausedByUser) stop(); else { rows.forEach(row => { row.position = row.viewport.scrollLeft; }); previousTime = 0; resume(); } }, {signal});
      document.addEventListener('visibilitychange', () => { if (document.hidden) stop(); else resume(); }, {signal});
      window.addEventListener('pagehide', stop, {signal});
      window.addEventListener('pageshow', resume, {signal});
      const observer = new IntersectionObserver(entries => {
        inView = entries[0]?.isIntersecting || false;
        if (!inView) stop(); else {
          rows.forEach((row,index) => { cardObservers[index].disconnect(); row.cards.forEach(card => cardObservers[index].observe(card)); }); resume();
        }
      }, {threshold:0.01});
      const rowObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => { const row = rows.find(item => item.viewport === entry.target); if (row) row.visible = entry.isIntersecting && entry.intersectionRatio > 0; });
        if (!rows.some(row => row.visible && !row.focus)) stop(); else resume();
      }, {threshold:0.01});
      const resize = new ResizeObserver(measure);
      rows.forEach(row => { resize.observe(row.original); resize.observe(row.viewport); });
      motion.addEventListener('change', configure);
      document.addEventListener('shopify:section:unload', event => {
        if (!event.target.contains(section)) return;
        destroyed = true; stop(); events.abort(); observer.disconnect(); rowObserver.disconnect(); resize.disconnect(); cardObservers.forEach(item => item.disconnect()); motion.removeEventListener('change', configure); delete section.dataset.marqueeReady;
      }, {signal});
      configure(); observer.observe(section); rows.forEach(row => rowObserver.observe(row.viewport));
    });
  }
  init(); document.addEventListener('shopify:section:load', event => init(event.target));
})();
